import * as THREE from 'three'
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import { useAppStore } from '@core/store'
import { rawIfc } from './rawIfc'
import { viewerApi } from './viewerApi'

// Revit-style "Levels and Grids" overlay. Two extra Three groups are
// attached to the world scene root, one per concept:
//
//   __vn2rLevels  - IfcBuildingStorey horizontal outline + label
//   __vn2rGrids   - IfcGrid axis polylines + label per IfcGridAxis
//
// IMPORTANT: a previous version of this module triggered a "black
// screen on zoom" regression. The hypothesis was that the line
// geometries extended the scene's bounding sphere, and the
// SimpleCamera / OrbitControls then recomputed near/far in a way
// that lost depth precision at high zoom. Defences in this version:
//
//   - Each LineSegments geometry has its boundingBox /
//     boundingSphere PINNED to a tiny sphere at origin, so neither
//     three.js nor the scene helpers consider the line for
//     camera-fitting purposes. (frustumCulled=false on top, so they
//     still render no matter where the camera looks.)
//   - The overlay groups have raycast=()=>void (so picking ignores
//     them) and matrixAutoUpdate=false.
//   - getModelBbox reads strictly from FragmentsModel objects,
//     never the scene root, so re-running the overlay can never
//     compound itself.
//   - Sanity guard on bbox: skip if any extent > 1e6 (almost always
//     means a corrupt IfcMapConversion or stray geometry).
//   - try/catch around each public function; failures log to
//     console.warn and the toggle still works (group is cleared).

const LEVELS_GROUP = '__vn2rLevels'
const GRIDS_GROUP = '__vn2rGrids'
const PAD = 5
const ZERO_SPHERE = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 0)
const ZERO_BOX = new THREE.Box3(
  new THREE.Vector3(0, 0, 0),
  new THREE.Vector3(0, 0, 0),
)

function pinBounds(geom: THREE.BufferGeometry): void {
  // Three.js auto-computes geometry bounds from the position buffer
  // when not provided. Setting them explicitly to a zero-sized box
  // means any consumer that asks "how big is this object?" sees
  // empty, including scene-bbox helpers used by the camera.
  geom.boundingBox = ZERO_BOX.clone()
  geom.boundingSphere = ZERO_SPHERE.clone()
}

function getOrCreateGroup(name: string): THREE.Group | null {
  const world = viewerApi.getWorld()
  if (!world) return null
  const scene = world.scene.three
  let g = scene.getObjectByName(name) as THREE.Group | null
  if (!g) {
    g = new THREE.Group()
    g.name = name
    g.renderOrder = 1000
    g.raycast = () => {}
    g.matrixAutoUpdate = false
    scene.add(g)
  }
  return g
}

function clearGroup(name: string): void {
  const world = viewerApi.getWorld()
  if (!world) return
  const scene = world.scene.three
  const g = scene.getObjectByName(name)
  if (!g) return
  g.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.geometry) m.geometry.dispose()
    if ((m as THREE.Mesh).material) {
      const mat = (m as THREE.Mesh).material
      if (Array.isArray(mat)) mat.forEach((mm) => mm.dispose())
      else (mat as THREE.Material).dispose()
    }
    // CSS2DObject.element is a DOM node attached to the
    // CSS2DRenderer's wrapper. Removing the Object3D from the scene
    // is NOT enough: the renderer only cleans the DOM on its next
    // render pass, and even then only if the wrapper still has a
    // matching Object3D. Pull the node out of the DOM directly so
    // labels disappear immediately when the toggle goes off.
    const maybeCss2d = o as { element?: unknown }
    if (
      maybeCss2d.element instanceof HTMLElement &&
      maybeCss2d.element.parentNode
    ) {
      maybeCss2d.element.parentNode.removeChild(maybeCss2d.element)
    }
  })
  scene.remove(g)
}

function getModelBbox(): THREE.Box3 | null {
  const box = new THREE.Box3()
  let any = false
  for (const [, fm] of viewerApi.iterModels()) {
    if (fm.object) {
      const sub = new THREE.Box3().setFromObject(fm.object)
      if (!sub.isEmpty()) {
        box.union(sub)
        any = true
      }
    }
  }
  return any ? box : null
}

interface LabelOptions {
  text: string
  color: string
  /** Click handler (selecciona elementos do storey, etc.). */
  onClick?: () => void
  /** Title to show on the click handler tooltip. */
  clickTitle?: string
  /** When true, the user can drag the label across the floor plane.
   * Drag delta is converted from screen pixels to world units via
   * the camera projection; the label keeps its Y so it stays at
   * storey level. */
  draggable?: boolean
  /** Called on every drag step with the new world position. Lets the
   * caller update a leader line (or any other dependent object). */
  onDrag?: (pos: THREE.Vector3) => void
}

function makeLabel(opts: LabelOptions): CSS2DObject {
  const div = document.createElement('div')
  div.className = 'vn2r-level-label'
  div.textContent = opts.text
  div.style.color = opts.color
  div.style.fontFamily = 'var(--font-sans, system-ui, sans-serif)'
  div.style.fontSize = '11px'
  div.style.fontWeight = '600'
  div.style.padding = '1px 5px'
  div.style.background = 'rgba(11, 11, 12, 0.78)'
  div.style.border = '1px solid ' + opts.color
  div.style.borderRadius = '3px'
  div.style.whiteSpace = 'nowrap'
  div.style.userSelect = 'none'
  // CSS2DObject DOM nodes default to pointer-events: none in the
  // CSS2DRenderer wrapper. We re-enable per element when interactive.
  if (opts.onClick || opts.draggable) {
    div.style.pointerEvents = 'auto'
    div.style.cursor = opts.draggable ? 'grab' : 'pointer'
  } else {
    div.style.pointerEvents = 'none'
  }
  if (opts.clickTitle) div.title = opts.clickTitle

  const obj = new CSS2DObject(div)

  if (opts.onClick && !opts.draggable) {
    div.addEventListener('click', (e) => {
      e.stopPropagation()
      opts.onClick!()
    })
  }
  if (opts.draggable) {
    let dragging = false
    let didMove = false
    let startScreen: { x: number; y: number } | null = null
    let startWorld: THREE.Vector3 | null = null
    const onPointerDown = (e: PointerEvent) => {
      dragging = true
      didMove = false
      startScreen = { x: e.clientX, y: e.clientY }
      startWorld = obj.position.clone()
      div.setPointerCapture?.(e.pointerId)
      div.style.cursor = 'grabbing'
      e.stopPropagation()
    }
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging || !startScreen || !startWorld) return
      const dx = e.clientX - startScreen.x
      const dy = e.clientY - startScreen.y
      if (Math.abs(dx) + Math.abs(dy) > 3) didMove = true
      // Convert screen-pixel delta to world delta on the floor
      // plane (Y fixed = storey level). Use the camera distance to
      // the label's Y plane for a uniform scale that matches the
      // visible drag.
      const world = viewerApi.getWorld()
      if (!world || !world.renderer) return
      const cam = world.camera.three
      const dom = world.renderer.three.domElement
      const heightAtPlane = (() => {
        const camPos = cam.position
        const dist = Math.abs(camPos.y - startWorld.y) || 1
        const fov = (cam as THREE.PerspectiveCamera).fov
        if (typeof fov === 'number') {
          return 2 * Math.tan((fov * Math.PI) / 360) * dist
        }
        return dist
      })()
      const worldPerPx = heightAtPlane / dom.clientHeight
      // Camera-aware horizontal axis: project camera "right" vector
      // onto the world XZ plane so dragging right moves the label
      // along the user's apparent right-hand direction.
      const right = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld, 0)
      right.y = 0
      right.normalize()
      const fwd = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), right)
      obj.position.set(
        startWorld.x + right.x * dx * worldPerPx + fwd.x * dy * worldPerPx,
        startWorld.y,
        startWorld.z + right.z * dx * worldPerPx + fwd.z * dy * worldPerPx,
      )
      if (opts.onDrag) opts.onDrag(obj.position)
      viewerApi._invalidate()
    }
    const onPointerUp = (e: PointerEvent) => {
      if (!dragging) return
      dragging = false
      div.releasePointerCapture?.(e.pointerId)
      div.style.cursor = 'grab'
      // Click-after-drag distinction: if pointer barely moved, treat
      // as click so the user can also use the label as a select
      // affordance even when draggable=true.
      if (!didMove && opts.onClick) opts.onClick()
    }
    div.addEventListener('pointerdown', onPointerDown)
    div.addEventListener('pointermove', onPointerMove)
    div.addEventListener('pointerup', onPointerUp)
    div.addEventListener('pointercancel', onPointerUp)
  }

  return obj
}

export async function applyLevelsOverlay(visible: boolean): Promise<void> {
  try {
    clearGroup(LEVELS_GROUP)
    if (!visible) {
      viewerApi._invalidate()
      return
    }
    const group = getOrCreateGroup(LEVELS_GROUP)
    if (!group) return
    const bbox = getModelBbox()
    if (!bbox) return
    const dx = bbox.max.x - bbox.min.x
    const dz = bbox.max.z - bbox.min.z
    if (!Number.isFinite(dx) || !Number.isFinite(dz) || dx > 1e6 || dz > 1e6) {
      console.warn('[levelsAndGrids] bbox too large for Levels overlay', bbox)
      return
    }
    const x0 = bbox.min.x - PAD
    const x1 = bbox.max.x + PAD
    const z0 = bbox.min.z - PAD
    const z1 = bbox.max.z + PAD
    const lineMat = new THREE.LineBasicMaterial({
      color: 0x60a5fa,
      transparent: true,
      opacity: 0.55,
      depthTest: true,
      depthWrite: false,
    })

    for (const [modelId, fragModel] of viewerApi.iterModels()) {
      const storeyIds = rawIfc.getLineIDsByType(modelId, 'IFCBUILDINGSTOREY')
      if (storeyIds.length === 0) continue
      // Calibrate the Y-offset Fragments adds to the IFC's
      // Storey.Elevation. Elevation is the IFC's authored value
      // (correct by definition); Fragments places geometry at
      // Y = Elevation + offset, where offset accounts for any
      // IfcSite / IfcMapConversion translation Fragments did or
      // didn't apply. We sample ONE storey + its first contained
      // element and derive offset = bbox.min.y - storey.Elevation.
      let yOffset = 0
      let calibrated = false
      for (const sid of storeyIds) {
        if (calibrated) break
        const e = rawIfc.getEntity(modelId, sid) as
          | { Elevation?: unknown }
          | null
        const ev = e ? readScalar(e.Elevation) : null
        if (ev == null) continue
        const ids = rawIfc.getContainedElements(modelId, sid)
        if (ids.length === 0) continue
        const boxes = (await fragModel
          .getBoxes(ids.slice(0, 50))
          .catch(() => [])) as Array<THREE.Box3 | null>
        let minY = Infinity
        for (const b of boxes) {
          if (b && Number.isFinite(b.min.y)) minY = Math.min(minY, b.min.y)
        }
        if (Number.isFinite(minY)) {
          yOffset = minY - ev
          calibrated = true
          console.info(
            `[levelsAndGrids] Levels Y-offset calibrated against storey #${sid}: ${yOffset.toFixed(3)}`,
          )
        }
      }

      for (const sid of storeyIds) {
        const e = rawIfc.getEntity(modelId, sid) as
          | {
              Elevation?: unknown
              Name?: { value?: unknown } | string
            }
          | null
        if (!e) continue
        const ev = readScalar(e.Elevation)
        if (ev == null) continue
        const y = ev + yOffset
        // Per-storey rectangle: prefer the bbox of the storey's
        // contained elements (so each level rect tightly hugs the
        // geometry of THAT storey instead of stretching across the
        // whole model). Falls back to the model bbox when the
        // storey has no geometric contents.
        const containedIds = rawIfc.getContainedElements(modelId, sid)
        let sx0 = x0
        let sx1 = x1
        let sz0 = z0
        let sz1 = z1
        if (containedIds.length > 0) {
          const sample = containedIds.slice(0, 100)
          const boxes = (await fragModel
            .getBoxes(sample)
            .catch(() => [])) as Array<THREE.Box3 | null>
          const sb = new THREE.Box3()
          let any = false
          for (const b of boxes) {
            if (b && Number.isFinite(b.min.x)) {
              sb.union(b)
              any = true
            }
          }
          if (any && Number.isFinite(sb.min.x) && Number.isFinite(sb.max.x)) {
            sx0 = sb.min.x - PAD
            sx1 = sb.max.x + PAD
            sz0 = sb.min.z - PAD
            sz1 = sb.max.z + PAD
          }
        }
        const geom = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(sx0, y, sz0),
          new THREE.Vector3(sx1, y, sz0),
          new THREE.Vector3(sx1, y, sz1),
          new THREE.Vector3(sx0, y, sz1),
          new THREE.Vector3(sx0, y, sz0),
        ])
        pinBounds(geom)
        const line = new THREE.Line(geom, lineMat)
        line.frustumCulled = false
        line.matrixAutoUpdate = false
        group.add(line)

        let name: string | null = null
        const na = e.Name
        if (typeof na === 'string') name = na
        else if (
          na &&
          typeof na === 'object' &&
          typeof (na as { value?: unknown }).value === 'string'
        ) {
          name = (na as { value: string }).value
        }
        const storeyModelId = modelId

        // Leader line: a thin segment from the rectangle anchor
        // (x1, y, z0) to wherever the user drags the label. While
        // the label sits at the anchor the segment has zero length
        // and is invisible; as the label moves away, a guide line
        // grows so the user always knows which level the label
        // belongs to.
        const anchor = new THREE.Vector3(sx1, y, sz0)
        const leaderGeom = new THREE.BufferGeometry().setFromPoints([
          anchor.clone(),
          anchor.clone(),
        ])
        pinBounds(leaderGeom)
        const leader = new THREE.Line(leaderGeom, lineMat)
        leader.frustumCulled = false
        leader.matrixAutoUpdate = false
        group.add(leader)

        const label = makeLabel({
          text: `${name ?? 'Storey #' + sid} · y=${y.toFixed(2)}`,
          color: '#60a5fa',
          draggable: true,
          clickTitle:
            containedIds.length > 0
              ? `Selecionar os ${containedIds.length} elemento(s) deste piso (arrastar para reposicionar a etiqueta)`
              : 'Piso sem elementos contidos (arrastar para reposicionar a etiqueta)',
          onClick:
            containedIds.length === 0
              ? undefined
              : () => {
                  const targets = containedIds.map((id) => ({
                    modelId: storeyModelId,
                    localId: id,
                  }))
                  useAppStore.getState().applyBulkSelection(targets, 'replace')
                },
          onDrag: (pos) => {
            // Update the leader's second vertex live so the line
            // follows the label as it moves.
            const arr = leaderGeom.attributes.position.array as Float32Array
            arr[3] = pos.x
            arr[4] = pos.y
            arr[5] = pos.z
            leaderGeom.attributes.position.needsUpdate = true
          },
        })
        label.position.copy(anchor)
        group.add(label)
      }
    }
    viewerApi._invalidate()
  } catch (err) {
    console.warn('[levelsAndGrids] Levels overlay failed', err)
  }
}

export async function applyGridsOverlay(visible: boolean): Promise<void> {
  try {
    clearGroup(GRIDS_GROUP)
    if (!visible) {
      viewerApi._invalidate()
      return
    }
    const group = getOrCreateGroup(GRIDS_GROUP)
    if (!group) return
    const lineMat = new THREE.LineBasicMaterial({
      color: 0xfbbf24,
      transparent: true,
      opacity: 0.65,
      depthTest: true,
      depthWrite: false,
    })

    // Per-storey filter: when the user picked specific storeys, only
    // render IfcGrids whose containing storey is in the whitelist.
    // Empty whitelist = render all grids.
    const storeyFilter = useAppStore.getState().gridsStoreyFilter
    const filterActive = storeyFilter.length > 0
    const allowedStoreys = new Set(storeyFilter)

    let totalGrids = 0
    let totalAxes = 0
    let renderedAxes = 0
    let skippedByFilter = 0
    let firstSampleLogged = false
    for (const [modelId, fragModel] of viewerApi.iterModels()) {
      const gridIds = rawIfc.getLineIDsByType(modelId, 'IFCGRID')
      if (gridIds.length === 0) continue
      totalGrids += gridIds.length

      // Build a quick lookup grid expressID → containing storey
      // expressID via the same containment map we already cache
      // (the value side of IfcRelContainedInSpatialStructure).
      // ensureContainmentMap returns parent → [children]; we
      // invert it once into a child → parent table here.
      const containmentByGrid = new Map<number, number>()
      if (filterActive) {
        const allStoreys = rawIfc.getLineIDsByType(
          modelId,
          'IFCBUILDINGSTOREY',
        )
        for (const sid of allStoreys) {
          const ids = rawIfc.getContainedElements(modelId, sid)
          for (const id of ids) containmentByGrid.set(id, sid)
        }
      }

      // Per-model 3D offset between "what we'd compute via the
      // IfcLocalPlacement chain" and "where Fragments actually
      // drew the geometry". Without this, applying the grid's
      // chain matrix lands the grids at IFC world coords
      // (potentially +1.4M from the IfcMapConversion translation)
      // while Fragments may have recentred the model around
      // origin. Sample one element with a known IFC world coord
      // (via its chain) and compare with its Fragments bbox
      // centre.
      const offset = new THREE.Vector3(0, 0, 0)
      let calibrated = false
      const storeyIds = rawIfc.getLineIDsByType(modelId, 'IFCBUILDINGSTOREY')
      for (const sid of storeyIds) {
        if (calibrated) break
        const ids = rawIfc.getContainedElements(modelId, sid)
        if (ids.length === 0) continue
        const sampleId = ids[0]!
        const m = buildWorldMatrix(modelId, sampleId)
        const ifcOrigin = new THREE.Vector3(0, 0, 0).applyMatrix4(m)
        const expectedThree = ifcToThree(ifcOrigin)
        const boxes = (await fragModel
          .getBoxes([sampleId])
          .catch(() => [])) as Array<THREE.Box3 | null>
        const b = boxes[0]
        if (!b) continue
        const actual = new THREE.Vector3()
        b.getCenter(actual)
        offset.copy(actual).sub(expectedThree)
        calibrated = true
        console.info(
          `[levelsAndGrids] Grids offset calibrated against #${sampleId}: (${offset.x.toFixed(2)}, ${offset.y.toFixed(2)}, ${offset.z.toFixed(2)})`,
        )
      }

      for (const gid of gridIds) {
        if (filterActive) {
          const ownerStorey = containmentByGrid.get(gid)
          if (ownerStorey == null || !allowedStoreys.has(ownerStorey)) {
            skippedByFilter++
            continue
          }
        }
        const grid = rawIfc.getEntity(modelId, gid) as
          | {
              UAxes?: unknown[]
              VAxes?: unknown[]
              WAxes?: unknown[]
            }
          | null
        if (!grid) continue
        // Apply the IfcGrid's ObjectPlacement chain to lift each
        // axis vertex from grid-local coordinates to IFC world
        // coordinates (which Fragments uses too; it applies the
        // site rotation, as the visible model orientation confirms).
        // The matrix is in IFC convention (Z-up); we swap to three.js
        // (Y-up) at the end via ifcToThree.
        const gridMatrix = buildWorldMatrix(modelId, gid)
        const axes = [
          ...(Array.isArray(grid.UAxes) ? grid.UAxes : []),
          ...(Array.isArray(grid.VAxes) ? grid.VAxes : []),
          ...(Array.isArray(grid.WAxes) ? grid.WAxes : []),
        ]
        totalAxes += axes.length
        for (const ax of axes) {
          if (!ax || typeof ax !== 'object') continue
          const a = ax as {
            AxisCurve?: unknown
            AxisTag?: { value?: unknown } | string
          }
          if (!firstSampleLogged && a.AxisCurve) {
            console.info(
              '[levelsAndGrids] sample IfcGridAxis:',
              JSON.stringify(a, null, 2).slice(0, 1500),
            )
            firstSampleLogged = true
          }
          // parseAxisCurve already returns three.js Vector3 with the
          // IFC Z-up → three Y-up swap baked in. We need to UNDO
          // that swap before applying the IFC-space placement
          // matrix, then redo it. Cleaner approach: build an
          // unswapped vec, apply matrix, then swap.
          const localPts = parseAxisCurveIfc(a.AxisCurve)
          if (localPts.length < 2) continue
          const v3s: THREE.Vector3[] = []
          for (const local of localPts) {
            const ifcWorld = local.clone().applyMatrix4(gridMatrix)
            v3s.push(ifcToThree(ifcWorld).add(offset))
          }
          let extent = 0
          for (const v of v3s) {
            extent = Math.max(extent, Math.abs(v.x), Math.abs(v.y), Math.abs(v.z))
          }
          if (!Number.isFinite(extent) || extent > 1e6) continue
          const geom = new THREE.BufferGeometry().setFromPoints(v3s)
          pinBounds(geom)
          const line = new THREE.Line(geom, lineMat)
          line.frustumCulled = false
          line.matrixAutoUpdate = false
          group.add(line)
          let tag: string | null = null
          const t = a.AxisTag
          if (typeof t === 'string') tag = t
          else if (
            t &&
            typeof t === 'object' &&
            typeof (t as { value?: unknown }).value === 'string'
          ) {
            tag = (t as { value: string }).value
          }
          if (tag) {
            const label = makeLabel({
              text: tag,
              color: '#fbbf24',
              draggable: true,
              clickTitle: `IfcGridAxis ${tag} (arrastar para reposicionar)`,
            })
            label.position.copy(v3s[0]!)
            group.add(label)
          }
          renderedAxes++
        }
      }
    }
    console.info(
      `[levelsAndGrids] Grids overlay: ${totalGrids} IfcGrid(s) (${skippedByFilter} skipped by storey filter), ${totalAxes} axes inspected, ${renderedAxes} rendered.`,
    )
    if (totalAxes > 0 && renderedAxes === 0) {
      console.warn(
        '[levelsAndGrids] No grid axes rendered. See the sample log above. The IFC most likely uses IfcIndexedPolyCurve or IfcLine instead of IfcPolyline (the only shape this parser currently understands).',
      )
    }
    viewerApi._invalidate()
  } catch (err) {
    console.warn('[levelsAndGrids] Grids overlay failed', err)
  }
}

// IFC-coordinate version of parseAxisCurve (no Z-up→Y-up swap).
// Used when we need to apply an IFC-space placement matrix BEFORE
// converting to three.js coords.
function parseAxisCurveIfc(curve: unknown): THREE.Vector3[] {
  if (!curve || typeof curve !== 'object') return []
  const c = curve as { type?: unknown; Points?: unknown }
  const ctype = typeof c.type === 'string' ? c.type : ''
  if (Array.isArray(c.Points)) {
    const out: THREE.Vector3[] = []
    for (const p of c.Points) {
      if (!p || typeof p !== 'object') continue
      const co = (p as { Coordinates?: unknown[] }).Coordinates
      if (!Array.isArray(co)) continue
      const x = readScalar(co[0])
      const y = readScalar(co[1])
      const z = readScalar(co[2]) ?? 0
      if (x == null || y == null) continue
      out.push(new THREE.Vector3(x, y, z))
    }
    return out
  }
  if (
    ctype === 'IfcIndexedPolyCurve' ||
    (c.Points && typeof c.Points === 'object')
  ) {
    const ptList = c.Points as
      | { type?: unknown; CoordList?: unknown[] }
      | undefined
    if (!ptList || !Array.isArray(ptList.CoordList)) return []
    const is3D =
      typeof ptList.type === 'string' && ptList.type.endsWith('3D')
    const out: THREE.Vector3[] = []
    for (const tuple of ptList.CoordList) {
      if (!Array.isArray(tuple)) continue
      const x = readScalar(tuple[0])
      const y = readScalar(tuple[1])
      const z = is3D ? (readScalar(tuple[2]) ?? 0) : 0
      if (x == null || y == null) continue
      out.push(new THREE.Vector3(x, y, z))
    }
    return out
  }
  return []
}

// Convert an IfcGridAxis.AxisCurve into a polyline of three.js
// Vector3 (mapping IFC Z up → three.js Y up). Supports the two
// shapes Revit / Tekla / IFC4 exporters use:
//
//   IfcPolyline.Points = [IfcCartesianPoint, ...]
//                        each with .Coordinates = [IfcLengthMeasure...]
//
//   IfcIndexedPolyCurve.Points = IfcCartesianPointListND
//                        with .CoordList = [[x, y], [x, y], ...]
//                        and optional .Segments selecting subsequences.
//
// Other curve types (IfcLine, IfcCircle, ...) are not handled:
// returns [] so the caller skips them silently.
// Build a four-by-four matrix from an IfcAxis2Placement3D entity.
// Convention is IFC's (Z-up). Caller is responsible for the final
// IFC-Z-up → three.js-Y-up swap on points.
function matrixFromAxis2Placement(p: unknown): THREE.Matrix4 {
  const m = new THREE.Matrix4()
  if (!p || typeof p !== 'object') return m
  const placement = p as {
    Location?: { Coordinates?: unknown[] }
    Axis?: { DirectionRatios?: unknown[] }
    RefDirection?: { DirectionRatios?: unknown[] }
  }
  const loc = placement.Location?.Coordinates
  const tx = (loc && readScalar(loc[0])) ?? 0
  const ty = (loc && readScalar(loc[1])) ?? 0
  const tz = (loc && readScalar(loc[2])) ?? 0

  const axis = placement.Axis?.DirectionRatios
  const zAxis = new THREE.Vector3(0, 0, 1)
  if (axis && Array.isArray(axis)) {
    const ax = readScalar(axis[0]) ?? 0
    const ay = readScalar(axis[1]) ?? 0
    const az = readScalar(axis[2]) ?? 1
    zAxis.set(ax, ay, az).normalize()
  }

  const ref = placement.RefDirection?.DirectionRatios
  const xAxis = new THREE.Vector3(1, 0, 0)
  if (ref && Array.isArray(ref)) {
    const rx = readScalar(ref[0]) ?? 1
    const ry = readScalar(ref[1]) ?? 0
    const rz = readScalar(ref[2]) ?? 0
    xAxis.set(rx, ry, rz)
  }
  // Project the supplied X onto the plane perpendicular to Z so the
  // basis is orthogonal even when the IFC author was loose.
  const xDotZ = xAxis.dot(zAxis)
  xAxis.addScaledVector(zAxis, -xDotZ).normalize()
  const yAxis = new THREE.Vector3().crossVectors(zAxis, xAxis).normalize()

  m.makeBasis(xAxis, yAxis, zAxis)
  m.setPosition(tx, ty, tz)
  return m
}

// Walk the IfcLocalPlacement chain (leaf → root) and accumulate
// into a single world matrix. Each entry's RelativePlacement is in
// the parent's coordinate system, so we PRE-multiply each local
// matrix into the running world matrix:
//   m_world = root * ... * grandparent * parent * leaf
function buildWorldMatrix(modelId: string, expressID: number): THREE.Matrix4 {
  const m = new THREE.Matrix4()
  const chain = rawIfc.getPlacementChain(modelId, expressID)
  for (const p of chain) {
    if (!p || typeof p !== 'object') continue
    const rp = (p as { RelativePlacement?: unknown }).RelativePlacement
    if (!rp) continue
    const local = matrixFromAxis2Placement(rp)
    m.premultiply(local)
  }
  return m
}

// IFC Z-up world coords → three.js Y-up world coords via a proper
// rotation (NOT a swap). The transform is "rotate -90° around X
// axis" so handedness is preserved:
//
//   |x|     | 1  0  0 |   |x|     |  x |
//   |y|  →  | 0  0  1 | × |y|  =  |  z |
//   |z|     | 0 -1  0 |   |z|     | -y |
//
// A bare swap (x, z, y) flipped handedness, which made grid axes
// appear rotated in the wrong direction relative to the model.
function ifcToThree(v: THREE.Vector3): THREE.Vector3 {
  return new THREE.Vector3(v.x, v.z, -v.y)
}

function readScalar(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (
    v &&
    typeof v === 'object' &&
    typeof (v as { value?: unknown }).value === 'number'
  ) {
    return (v as { value: number }).value
  }
  return null
}
